/**
 * The family's "Ideas for you" (ui-ux-guideline §2.9 item 2, 2026-09-18).
 *
 * The parent home used to show these as chips in the companion rail: three
 * labels the platform generates for the family (`POST /chat/suggestions`,
 * each with a `kind`), or four static chips while they load. Same two
 * sources, one list under the composer, each row with the Lucide icon its
 * kind earns — the staff home's `WilliamsIdeas` shape.
 *
 * Picking a row sends its words to the companion. The old chips' `href`s
 * (the care hub tabs, /tasks, /community) are not followed: the composer is
 * the door, and every destination is one tap away on the sidebar.
 */
import type { LucideIcon } from 'lucide-react';
import {
  CalendarDays,
  CheckSquare,
  HeartPulse,
  Megaphone,
  MessageSquare,
  Pill,
  Sparkles,
  Stethoscope,
} from 'lucide-react';

export interface ParentIdea {
  id: string;
  icon: LucideIcon;
  /** The row's words — also what is sent when the row is picked. */
  label: string;
  message: string;
}

/** The suggestion kinds the backend may return (`allowedKinds` in routes/chat.js). */
const ICON_BY_KIND: Record<string, LucideIcon> = {
  ask_companion: MessageSquare,
  log_symptom: HeartPulse,
  log_medication: Pill,
  doctor_question: Stethoscope,
  prep_appointment: Stethoscope,
  open_tasks: CheckSquare,
  visit_community: Megaphone,
  view_calendar: CalendarDays,
};

/** The static chips carry a destination, not a kind; the icon follows the destination. */
const ICON_BY_HREF: Array<[prefix: string, icon: LucideIcon]> = [
  ['/medical?tab=questions', Stethoscope],
  ['/medical?tab=symptoms', HeartPulse],
  ['/medical?tab=medications', Pill],
  ['/tasks', CheckSquare],
  ['/community', Megaphone],
  ['/calendar', CalendarDays],
  ['/chat', MessageSquare],
];

function iconForHref(href: string): LucideIcon {
  const hit = ICON_BY_HREF.find(([prefix]) => href.startsWith(prefix));
  return hit ? hit[1] : Sparkles;
}

export function buildParentIdeas(args: {
  /** The generated suggestions, when they have landed. */
  suggestions?: Array<{ label: string; kind: string }> | null;
  /** The static chips (`buildChips`) that stand in until they do. */
  fallback: Array<{ label: string; href: string }>;
}): ParentIdea[] {
  const generated = (args.suggestions ?? []).filter((s) => s.label.trim().length > 0);
  if (generated.length > 0) {
    return generated.map((s, i) => ({
      id: `generated-${i}-${s.kind}`,
      icon: ICON_BY_KIND[s.kind] ?? Sparkles,
      label: s.label,
      message: s.label,
    }));
  }
  return args.fallback.map((c, i) => ({
    id: `fallback-${i}`,
    icon: iconForHref(c.href),
    label: c.label,
    message: c.label,
  }));
}
